import CharacterCounter from 'components/CharacterCounter'
import { buildTranslate } from 'locales'
import { baseKeymap } from 'prosemirror-commands'
import { history, redo, undo } from 'prosemirror-history'
import { keymap } from 'prosemirror-keymap'
import { EditorState, Plugin, Transaction } from 'prosemirror-state'
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view'
import React, { useEffect, useRef, useState } from 'react'
import { errorHandler } from 'utilities/errorHandler'
import { getDocHtml, parseHtmlToDoc } from './helpers'
import { generateEditorId } from './hooks/useLinkBubble'
import LinkBubble from './LinkBubble'
import './RichTextEditor.scss'
import { htmlToMarkdown, markdownToHtml } from './serialization'

const t = buildTranslate('components.rich_text_editor')

const DEFAULT_MAX_CHARS = 500

export type RichTextEditorProps = {
  initialContent: string
  onContentChange: (markdown: string, charCount: number) => void
  placeholder?: string
  maxChars?: number
  showCharCounter?: boolean
  className?: string
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialContent,
  onContentChange,
  placeholder,
  maxChars = DEFAULT_MAX_CHARS,
  showCharCounter = true,
  className,
}) => {
  const [charCount, setCharCount] = useState(0)
  const [editorError, setEditorError] = useState(false)
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const editorViewRef = useRef<EditorView | null>(null)
  const mountRef = useRef<HTMLDivElement>(null)
  const onContentChangeRef = useRef(onContentChange)
  onContentChangeRef.current = onContentChange
  const editorIdRef = useRef(generateEditorId())

  // --- Editor initialization ---

  useEffect(() => {
    if (!mountRef.current) return

    try {
      const doc = parseHtmlToDoc(markdownToHtml(initialContent))

      const placeholderText = placeholder || 'Enter text...'

      const placeholderDecorations = (state: EditorState) => {
        const { doc: d } = state
        if (
          d.childCount === 1 &&
          d.firstChild &&
          d.firstChild.isTextblock &&
          d.firstChild.content.size === 0
        ) {
          return DecorationSet.create(d, [
            Decoration.node(0, d.firstChild.nodeSize, {
              'data-placeholder': placeholderText,
              class: 'is-empty',
            }),
          ])
        }
        return DecorationSet.empty
      }

      const placeholderPlugin = new Plugin({
        state: {
          init(_, state) {
            return placeholderDecorations(state)
          },
          apply(tr, old, _, newState) {
            return tr.docChanged ? placeholderDecorations(newState) : old
          },
        },
        props: {
          decorations(state) {
            return (this as any).getState(state)
          },
        },
      })

      const charLimitPlugin = new Plugin({
        filterTransaction(tr: Transaction) {
          if (!tr.docChanged) return true
          return tr.doc.textContent.length <= maxChars
        },
      })

      const state = EditorState.create({
        doc,
        plugins: [
          history(),
          keymap({ 'Mod-z': undo, 'Mod-Shift-z': redo }),
          keymap(baseKeymap),
          placeholderPlugin,
          charLimitPlugin,
        ],
      })

      const view = new EditorView(mountRef.current, {
        state,
        dispatchTransaction(tr) {
          const newState = view.state.apply(tr)
          view.updateState(newState)

          if (tr.docChanged) {
            try {
              const html = getDocHtml(view)
              const markdown = htmlToMarkdown(html)
              const count = newState.doc.textContent.length
              onContentChangeRef.current(markdown, count)
              setCharCount(count)
            } catch (error) {
              errorHandler.capture('RichTextEditor content sync failed', {
                error,
              })
              setEditorError(true)
            }
          }
        },
        attributes: {
          class: 'ProseMirror',
        },
      })

      editorViewRef.current = view
      setCharCount(doc.textContent.length)

      return () => {
        view.destroy()
        editorViewRef.current = null
      }
    } catch (error) {
      errorHandler.capture('RichTextEditor initialization failed', { error })
      setEditorError(true)
      return () => {
        if (editorViewRef.current) {
          editorViewRef.current.destroy()
          editorViewRef.current = null
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // --- Render ---

  if (editorError) {
    return (
      <p className="text-danger" data-testid="editor-error">
        {t('editor_error')}
      </p>
    )
  }

  return (
    <div className={className}>
      {showCharCounter && (
        <div className="d-flex align-items-center justify-content-end mb-xs">
          <CharacterCounter
            currentCount={charCount}
            maxCount={maxChars}
            isError={charCount >= maxChars}
          />
        </div>
      )}
      <div ref={editorContainerRef} className="position-relative">
        <div
          className="rich-text-editor-wrapper"
          onClick={() => editorViewRef.current?.focus()}
        >
          <div ref={mountRef} className="rich-text-editor" />
        </div>
        <LinkBubble
          editorId={editorIdRef.current}
          editorViewRef={editorViewRef}
          editorContainerRef={editorContainerRef}
          mountRef={mountRef}
        />
      </div>
    </div>
  )
}

export default RichTextEditor
